import { ATTRIBUTES, type Attribute } from "../rules/attributes.ts";

/**
 * The marks a dialogue choice may end with (ADR-136, the author's ruling of 2026-10-28: icons at the end of the
 * option, not words), each to its icon key in the icon table (`tools/game-icons/icons.json`, kind `dialogue`).
 * Seven name an attribute check, one per attribute of Bible 4.1; the rest name a background or trait choice, a
 * choice whose needs are not met, the two weights and Leave.
 */
export const DIALOGUE_MARK_ICONS = {
  might: "dialogue-might",
  agility: "dialogue-agility",
  vitality: "dialogue-vitality",
  intellect: "dialogue-intellect",
  willpower: "dialogue-willpower",
  perception: "dialogue-perception",
  presence: "dialogue-presence",
  background: "dialogue-background",
  locked: "dialogue-locked",
  significant: "dialogue-significant",
  defining: "dialogue-defining",
  leave: "dialogue-leave",
} as const;

/**
 * One of the marks.
 */
export type DialogueMark = keyof typeof DIALOGUE_MARK_ICONS;

/**
 * The seven attributes of Bible 4.1, in its order: the marks of an attribute check (ADR-138 decision 1).
 */
export const DIALOGUE_CHECK_ATTRIBUTES = ATTRIBUTES;

/**
 * An attribute a dialogue check reads.
 */
export type DialogueCheckAttribute = Attribute;

/**
 * Whether a mark names an attribute check.
 *
 * @param mark The mark.
 * @returns True for the seven attribute marks.
 */
export function isCheckMark(mark: DialogueMark): mark is DialogueCheckAttribute {
  return (DIALOGUE_CHECK_ATTRIBUTES as readonly string[]).includes(mark);
}

/**
 * The marks in their wire order (ADR-136 amendment of 2026-10-28, decision 7): a dialogue frame's choice names each
 * mark by its place here. A later mark is appended, never inserted.
 */
export const DIALOGUE_MARKS = Object.freeze(Object.keys(DIALOGUE_MARK_ICONS) as DialogueMark[]);

/**
 * A mark's number on the wire.
 *
 * @param mark The mark.
 * @returns Its place in `DIALOGUE_MARKS`.
 */
export function markNumber(mark: DialogueMark): number {
  return DIALOGUE_MARKS.indexOf(mark);
}
